<template>
  <button
    :type="type"
    class="ui-icon-btn"
    :class="[`ui-icon-btn--${variant}`, `ui-icon-btn--${size}`]"
    :disabled="disabled"
    :aria-label="ariaLabel"
    :aria-disabled="disabled ? 'true' : undefined"
    @click="handleClick"
  >
    <span class="ui-icon-btn__icon" aria-hidden="true">
      <slot></slot>
    </span>
    <span v-if="badge != null" class="ui-icon-btn__badge" aria-hidden="true">{{ badge }}</span>
  </button>
</template>

<script>
export default {
  name: 'UiIconButton',
  props: {
    variant: {
      type: String,
      default: 'ghost',
      validator: v => ['ghost', 'solid', 'accent', 'glass'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    disabled: { type: Boolean, default: false },
    ariaLabel: {
      type: String,
      required: true,
      validator(v) {
        return !!(v && v.trim());
      },
    },
    badge: { type: [Number, String], default: null },
    type: { type: String, default: 'button' },
  },
  methods: {
    handleClick(e) {
      if (!this.disabled) {
        this.$emit('click', e);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ui-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: background var(--duration-fast) var(--easing-standard),
              opacity var(--duration-fast) var(--easing-standard);
  -webkit-user-select: none;
  user-select: none;
}

/* ── Sizes ── */
/* Visual sizes are distinct. 44×44 minimum hit area is provided via ::before pseudo-element. */
.ui-icon-btn--sm { width: 32px; height: 32px; }
.ui-icon-btn--md { width: 40px; height: 40px; }
.ui-icon-btn--lg { width: 48px; height: 48px; }

/* Extend hit area to 44×44 for sm and md without affecting layout */
.ui-icon-btn--sm::before,
.ui-icon-btn--md::before {
  content: '';
  position: absolute;
  inset: 50% auto auto 50%;
  width: 44px;
  height: 44px;
  transform: translate(-50%, -50%);
}

.ui-icon-btn__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ui-icon-btn--sm .ui-icon-btn__icon { font-size: 16px; width: 16px; height: 16px; }
.ui-icon-btn--md .ui-icon-btn__icon { font-size: 18px; width: 18px; height: 18px; }
.ui-icon-btn--lg .ui-icon-btn__icon { font-size: 22px; width: 22px; height: 22px; }

/* ── Variants ── */
.ui-icon-btn--ghost {
  background: transparent;
  color: var(--color-text);
}
.ui-icon-btn--ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}

.ui-icon-btn--solid {
  background: var(--color-surface3);
  color: var(--color-text);
}
.ui-icon-btn--solid:hover:not(:disabled) {
  background: var(--color-surface4);
}

.ui-icon-btn--accent {
  background: var(--color-accent);
  color: #ffffff;
}
.ui-icon-btn--accent:hover:not(:disabled) {
  background: var(--color-accentHover);
}

.ui-icon-btn--glass {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}
.ui-icon-btn--glass:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.14);
}

/* ── States ── */
.ui-icon-btn:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.ui-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ui-icon-btn:disabled:focus-visible {
  outline: none;
}

/* ── Badge ── */
.ui-icon-btn__badge {
  position: absolute;
  top: 6px;
  inset-inline-end: 6px;
  min-width: 16px;
  height: 16px;
  padding-inline: 4px;
  background: var(--color-accent);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  font-family: var(--font-mono);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

@media (prefers-reduced-motion: reduce) {
  .ui-icon-btn {
    transition: none;
  }
}
</style>
